import dynamic from 'next/dynamic'
import React, { useEffect, useRef } from 'react'
import { ArrowRight, CalendarDays, Gauge, PlayCircle, ShieldCheck, Zap } from 'lucide-react'
import { buttonClassName } from './button'

const Spline = dynamic(() => import('@splinetool/react-spline'), {
  ssr: false,
  loading: () => (
    <div className="flex h-full w-full items-center justify-center bg-slate-950">
      <div className="h-32 w-32 rounded-full border border-sky-300/30 bg-sky-300/10 blur-2xl" />
    </div>
  ),
})

function HeroSplineBackground() {
  return (
    <div className="relative h-full w-full overflow-hidden">
      <Spline
        style={{
          width: '100%',
          height: '100%',
          pointerEvents: 'auto',
        }}
        scene="https://prod.spline.design/us3ALejTXl6usHZ7/scene.splinecode"
      />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(15,23,42,0.96),rgba(15,23,42,0.68)_42%,rgba(15,23,42,0.35)_68%,rgba(15,23,42,0.9)),linear-gradient(to_bottom,rgba(15,23,42,0.12),rgba(15,23,42,0.92)_78%,#0f172a)]" />
    </div>
  )
}

function CapabilityRail() {
  const items = [
    { icon: Gauge, label: 'Systems diagnosis' },
    { icon: ShieldCheck, label: 'AI governance' },
    { icon: Zap, label: 'Execution velocity' },
  ]

  return (
    <div className="mt-10 grid max-w-2xl gap-3 sm:grid-cols-3">
      {items.map((item) => {
        const Icon = item.icon
        return (
          <div
            key={item.label}
            className="rounded-2xl border border-slate-700/80 bg-slate-950/60 p-4 text-sm font-semibold text-slate-200 backdrop-blur"
          >
            <Icon className="mb-3 h-5 w-5 text-sky-300" />
            {item.label}
          </div>
        )
      })}
    </div>
  )
}

export function HeroSection() {
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      const node = contentRef.current
      if (!node) return

      requestAnimationFrame(() => {
        const progress = Math.min(window.scrollY / 520, 1)
        node.style.transform = `translateY(${progress * 28}px)`
        node.style.opacity = String(1 - progress * 0.35)
      })
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <section className="relative min-h-[calc(100vh-69px)] overflow-hidden border-b border-slate-800 bg-slate-950">
      <div className="absolute inset-0 z-0">
        <HeroSplineBackground />
      </div>
      <div className="pointer-events-none absolute inset-0 z-0 circuit-grid opacity-35" aria-hidden="true" />

      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-69px)] max-w-7xl items-center px-5 py-20 sm:px-8">
        <div ref={contentRef} className="max-w-4xl will-change-transform">
          <p className="mb-5 font-mono text-sm font-semibold uppercase tracking-[0.24em] text-sky-300">
            Fractional CTO · AI Strategy · Systems Architecture
          </p>
          <h1 className="max-w-4xl text-5xl font-black leading-[0.96] tracking-tight text-white sm:text-6xl lg:text-7xl">
            Stop trying to scale a broken circuit.
          </h1>
          <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-200 sm:text-xl">
            I help founders, startups, and enterprise teams use AI correctly, fix the technical
            bottlenecks slowing growth, and build systems that are ready for real scale.
          </p>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <a
              href="/book"
              className={buttonClassName({ size: 'lg' })}
            >
              <CalendarDays className="h-4 w-4" />
              Schedule a circuit audit
            </a>
            <a
              href="#workshops"
              className={buttonClassName({ className: 'bg-slate-950/35 backdrop-blur', size: 'lg', variant: 'outline' })}
            >
              <PlayCircle className="h-4 w-4" />
              Explore AI workshops
            </a>
            <a
              href="/pricing"
              className={buttonClassName({ className: 'bg-slate-950/20 backdrop-blur', size: 'lg', variant: 'outline' })}
            >
              View pricing
            </a>
          </div>

          <CapabilityRail />

          <a
            href="#services"
            className="mt-10 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-slate-300 transition hover:text-sky-200"
          >
            View services
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  )
}
